import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../supabaseClient';
import Footer from '../components/Footer';

const GOLD = '#c8a04e';
const GA   = (a) => `rgba(200,160,78,${a})`;
const W    = (a) => `rgba(255,255,255,${a})`;

const NAV_LINKS = [
  { label: 'Features', href: '/#features' },
  { label: 'Pricing',  href: '/#pricing'  },
  { label: 'FAQ',      href: '/#faq'      },
];

function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open,     setOpen]     = useState(false);
  const [user,     setUser]     = useState(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', fn, { passive: true });
    fn();
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUser(data?.user ?? null));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, s) => setUser(s?.user ?? null));
    return () => subscription.unsubscribe();
  }, []);

  const lk      = { fontSize:13, fontWeight:500, color:W(0.45), textDecoration:'none', padding:'6px 12px', borderRadius:8, transition:'color 0.14s', whiteSpace:'nowrap', letterSpacing:'0.01em' };
  const lOn     = e => { e.currentTarget.style.color = W(0.9); };
  const lOff    = e => { e.currentTarget.style.color = W(0.45); };
  const pill    = { display:'inline-flex', alignItems:'center', padding:'8px 20px', borderRadius:10, background:'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)', border:`1px solid ${W(0.09)}`, boxShadow:'0 4px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.06)', color:W(0.82), fontSize:13, fontWeight:600, letterSpacing:'0.02em', textDecoration:'none', cursor:'pointer', transition:'all 0.15s' };
  const pillOn  = e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.045) 55%, rgba(255,255,255,0.025) 100%)'; e.currentTarget.style.borderColor = W(0.15); e.currentTarget.style.color = W(1); };
  const pillOff = e => { e.currentTarget.style.background = 'radial-gradient(ellipse 120% 80% at 50% 0%, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.025) 55%, rgba(255,255,255,0.015) 100%)'; e.currentTarget.style.borderColor = W(0.09); e.currentTarget.style.color = W(0.82); };

  return (
    <>
      <nav style={{ position:'fixed', top:0, left:0, right:0, zIndex:200, transition:'background 0.35s ease, border-color 0.35s ease, backdrop-filter 0.35s ease', background:scrolled ? 'rgba(0,0,0,0.75)' : 'transparent', backdropFilter:scrolled ? 'blur(20px)' : 'none', WebkitBackdropFilter:scrolled ? 'blur(20px)' : 'none', borderBottom:scrolled ? `1px solid ${W(0.07)}` : '1px solid transparent' }}>
        <div style={{ maxWidth:1120, margin:'0 auto', display:'grid', gridTemplateColumns:'1fr auto 1fr', alignItems:'center', gap:24, padding:'0 32px', height:68 }}>
          <Link to="/"><img src="/athena_logo.png" alt="Athena" style={{ height:64, width:64, display:'block', objectFit:'contain' }} /></Link>
          <div style={{ display:'flex', alignItems:'center', gap:2 }}>
            {NAV_LINKS.map(n => <a key={n.label} href={n.href} style={lk} onMouseEnter={lOn} onMouseLeave={lOff}>{n.label}</a>)}
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10, justifyContent:'flex-end' }}>
            {user
              ? <Link to="/dashboard" style={pill} onMouseEnter={pillOn} onMouseLeave={pillOff}>Dashboard</Link>
              : <>
                  <Link to="/login"    style={lk}                       onMouseEnter={lOn}    onMouseLeave={lOff}>Sign In</Link>
                  <Link to="/register" style={{ ...pill, color:W(0.55) }} onMouseEnter={pillOn} onMouseLeave={pillOff}>Sign Up</Link>
                </>
            }
          </div>
        </div>
        <AnimatePresence>
          {open && (
            <motion.div initial={{ height:0, opacity:0 }} animate={{ height:'auto', opacity:1 }} exit={{ height:0, opacity:0 }} transition={{ duration:0.2 }} style={{ overflow:'hidden', background:'rgba(0,0,0,0.96)', borderTop:`1px solid ${W(0.07)}` }}>
              <div style={{ padding:'14px 32px 22px', display:'flex', flexDirection:'column', gap:4 }}>
                {NAV_LINKS.map(n => <a key={n.label} href={n.href} onClick={() => setOpen(false)} style={{ ...lk, display:'block' }}>{n.label}</a>)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
      <div style={{ height:68 }} />
    </>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom:32, paddingBottom:32, borderBottom:`1px solid ${W(0.05)}` }}>
      <h2 style={{ fontSize:14, fontWeight:700, color:W(0.85), margin:'0 0 12px', letterSpacing:'-0.01em' }}>{title}</h2>
      <div style={{ fontSize:13.5, color:W(0.38), lineHeight:1.85 }}>{children}</div>
    </div>
  );
}

export default function RefundPolicy() {
  return (
    <div style={{ minHeight:'100vh', background:'#000', position:'relative' }}>
      <div style={{ position:'fixed', inset:0, pointerEvents:'none', zIndex:0, opacity:0.03, backgroundImage:`url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='256' height='256'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='256' height='256' filter='url(%23n)'/%3E%3C/svg%3E")` }} />
      <div style={{ position:'fixed', top:0, left:'20%', right:'20%', height:260, background:`radial-gradient(ellipse at 50% 0%, ${GA(0.06)} 0%, transparent 70%)`, pointerEvents:'none', zIndex:0 }} />
      <div style={{ position:'relative', zIndex:1 }}>
        <Navbar />
        <main style={{ maxWidth:720, margin:'0 auto', padding:'60px 24px 96px' }}>
          <motion.div initial={{ opacity:0, y:20 }} animate={{ opacity:1, y:0 }} transition={{ duration:0.45 }}>
            <p style={{ fontSize:10, fontWeight:700, color:W(0.2), letterSpacing:'0.22em', textTransform:'uppercase', margin:'0 0 14px' }}>Legal</p>
            <h1 style={{ fontSize:36, fontWeight:800, color:W(0.95), margin:'0 0 8px', letterSpacing:'-0.02em' }}>Refund Policy</h1>
            <p style={{ fontSize:13, color:W(0.25), margin:'0 0 40px' }}>Last updated: April 2026</p>

            <div style={{ position:'relative', background:'rgba(255,255,255,0.02)', border:`1px solid ${W(0.06)}`, borderRadius:20, padding:'40px 40px 8px' }}>
              <div style={{ position:'absolute', top:0, left:40, right:40, height:1, background:`linear-gradient(90deg, transparent, ${GA(0.18)}, transparent)` }} />

              <Section title="1. General Policy">
                <p>All purchases made through Athena are considered final. Due to the digital nature of our products — license keys for software — we generally do not offer refunds once a key has been issued to your account.</p>
              </Section>

              <Section title="2. Eligible Refunds">
                <p style={{ marginBottom:10 }}>We will offer a full refund if <strong style={{ color:W(0.6) }}>both</strong> of the following conditions are met:</p>
                <ul style={{ paddingLeft:18, margin:'0 0 10px' }}>
                  <li style={{ marginBottom:6 }}>You have experienced a technical issue with our software or service that we are <strong style={{ color:W(0.6) }}>unable to resolve</strong>.</li>
                  <li>Your refund request is submitted <strong style={{ color:W(0.6) }}>within 24 hours</strong> of the original purchase date.</li>
                </ul>
                <p>If both conditions are met, you are entitled to a full refund. Refunds are processed back to the original payment method and may take 5–10 business days to appear.</p>
              </Section>

              <Section title="3. Non-Eligible Refunds">
                <p style={{ marginBottom:10 }}>Refunds will <strong style={{ color:W(0.6) }}>not</strong> be issued in any of the following situations:</p>
                <ul style={{ paddingLeft:18, margin:0 }}>
                  <li style={{ marginBottom:6 }}>Your request is submitted more than 24 hours after purchase.</li>
                  <li style={{ marginBottom:6 }}>The issue you experienced has been resolved by our support team.</li>
                  <li style={{ marginBottom:6 }}>You were banned from a server or game as a result of using our software.</li>
                  <li style={{ marginBottom:6 }}>You changed your mind or no longer want the product.</li>
                  <li style={{ marginBottom:6 }}>You purchased the wrong plan by mistake.</li>
                  <li>Your license key has already been activated and used.</li>
                </ul>
              </Section>

              <Section title="4. How to Request a Refund">
                <p style={{ marginBottom:10 }}>Open a support ticket in our <a href="https://discord.gg/9KWNw8CeZX" target="_blank" rel="noopener noreferrer" style={{ color:GOLD, textDecoration:'none', borderBottom:`1px solid ${GA(0.3)}` }}>Discord server</a> or email us at <a href="mailto:support@athenascripts.cc" style={{ color:GOLD, textDecoration:'none', borderBottom:`1px solid ${GA(0.3)}` }}>support@athenascripts.cc</a> within 24 hours of your purchase. Please include:</p>
                <ul style={{ paddingLeft:18, margin:'0 0 10px' }}>
                  <li style={{ marginBottom:6 }}>Your account email address</li>
                  <li style={{ marginBottom:6 }}>Your Order ID (found in the Orders tab of your dashboard)</li>
                  <li>A description of the issue you are experiencing</li>
                </ul>
                <p>Our team will review your request and respond as soon as possible. If approved, the refund will be processed immediately.</p>
              </Section>

              <Section title="5. Abuse of Refund Policy">
                <p>Any attempt to abuse this refund policy — including filing fraudulent claims or chargebacks — will result in permanent account termination and may result in legal action.</p>
              </Section>

              <Section title="6. Contact">
                <p>For any questions about this policy, contact us via our <a href="https://discord.gg/9KWNw8CeZX" target="_blank" rel="noopener noreferrer" style={{ color:GOLD, textDecoration:'none', borderBottom:`1px solid ${GA(0.3)}` }}>Discord server</a> or email us at <a href="mailto:support@athenascripts.cc" style={{ color:GOLD, textDecoration:'none', borderBottom:`1px solid ${GA(0.3)}` }}>support@athenascripts.cc</a>.</p>
              </Section>
            </div>

            <div style={{ display:'flex', gap:24, marginTop:28 }}>
              <Link to="/terms"   style={{ fontSize:13, color:W(0.3), textDecoration:'none', transition:'color 0.15s' }} onMouseEnter={e => e.currentTarget.style.color=W(0.7)} onMouseLeave={e => e.currentTarget.style.color=W(0.3)}>Terms of Service</Link>
              <Link to="/privacy" style={{ fontSize:13, color:W(0.3), textDecoration:'none', transition:'color 0.15s' }} onMouseEnter={e => e.currentTarget.style.color=W(0.7)} onMouseLeave={e => e.currentTarget.style.color=W(0.3)}>Privacy Policy</Link>
            </div>
          </motion.div>
        </main>
        <Footer />
      </div>
    </div>
  );
}
